import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { WrappedFieldProps } from 'redux-form';
import { Field, formValueSelector } from 'redux-form';

import type { FormJobOutletContext } from 'af-root/scenes/Company/Jobs/Form/types';

import type WorkRequestDetailsUpsertVM from 'ab-viewModels/workRequest/workRequestDetailsUpsert.viewModel';

import { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';

import type { RootState } from 'af-reducers';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';

import PlainCheckbox from 'af-components/Controls/Checkbox';
import type JobUpsertFM from 'af-components/SharedForms/Job/formModel';

import { formSectionClass, formSectionClassColumn1, formSectionClassColumn2, formSectionClassRow } from '../../helpers';
import styles from '../../styles.module.scss';

const getAttributeName = (attribute: keyof WorkRequestDetailsUpsertVM) => attribute;

type OwnProps = {
	formName: string;
	isDisabled: boolean;
	change: FormJobOutletContext['change'];
};

type Props = OwnProps & ConnectedProps<typeof connector>;

type WorkTypeOption = {
	id: TimeSheetEntryWorkType;
	name: string;
	disabled?: boolean;
};

const requiredWorkType = TimeSheetEntryWorkType.JOB;

const workTypeOptions: WorkTypeOption[] = [
	{
		id: TimeSheetEntryWorkType.JOB,
		name: 'Job',
		disabled: true,
	},
	{
		id: TimeSheetEntryWorkType.TRAVEL,
		name: 'Travel',
	},
	{
		id: TimeSheetEntryWorkType.SHOP,
		name: 'Shop',
	},
];

const isOptionalPayrollWorkType = (workType: TimeSheetEntryWorkType): boolean => {
	return workType === TimeSheetEntryWorkType.TRAVEL || workType === TimeSheetEntryWorkType.SHOP;
};

const normalizeWorkTypes = (workTypes: Nullable<TimeSheetEntryWorkType[]>): TimeSheetEntryWorkType[] => {
	const normalizedWorkTypes: TimeSheetEntryWorkType[] = [requiredWorkType];

	if (!workTypes || workTypes.length === 0) {
		return normalizedWorkTypes;
	}

	for (const workType of workTypes ?? []) {
		if (!isOptionalPayrollWorkType(workType)) {
			continue;
		}

		if (normalizedWorkTypes.includes(workType)) {
			continue;
		}

		normalizedWorkTypes.push(workType);
	}

	return normalizedWorkTypes;
};

type PayrollDifferentialWorkTypesCheckboxesProps = WrappedFieldProps & {
	disabled: boolean;
	id: string;
	label: string;
};

const PrevailingWageDeductionSection: React.FC<Props> = ({ isDisabled, payrollDifferential, change }) => {
	const unionPrevailingWageStyle = React.useMemo(() => [formSectionClassColumn1, styles['job-form__tab-content__form-section__row__centered-column']].join(' '), []);

	const hasPrevailingWageDeduction = !!payrollDifferential;

	React.useEffect(() => {
		if (!payrollDifferential) {
			return;
		}

		const currentWorkTypes = payrollDifferential.workTypes ?? [];
		const normalizedWorkTypes = normalizeWorkTypes(currentWorkTypes);

		const shouldUpdateWorkTypes = (
			currentWorkTypes.length !== normalizedWorkTypes.length
			|| currentWorkTypes.some((workType, index) => workType !== normalizedWorkTypes[index])
		);

		if (shouldUpdateWorkTypes) {
			change('payrollDifferential.workTypes', normalizedWorkTypes);
		}
		// Don't want change here
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payrollDifferential]);

	const renderPayrollDifferentialWorkTypesCheckboxes = React.useCallback((fieldProps: PayrollDifferentialWorkTypesCheckboxesProps) => {
		const {
			input,
			disabled,
			id,
			label,
		} = fieldProps;

		const selectedWorkTypes = normalizeWorkTypes(Array.isArray(input.value) ? input.value as TimeSheetEntryWorkType[] : null);

		const isSelected = (workType: TimeSheetEntryWorkType): boolean => {
			return selectedWorkTypes.includes(workType);
		};

		const handleWorkTypeChange = (workType: TimeSheetEntryWorkType) => (event: React.ChangeEvent<HTMLInputElement>) => {
			if (workType === requiredWorkType) {
				return;
			}

			const checked = event.target.checked;
			const nextWorkTypes = selectedWorkTypes.filter((_workType) => _workType !== workType);

			if (checked) {
				nextWorkTypes.push(workType);
			}

			input.onChange(normalizeWorkTypes(nextWorkTypes));
		};

		return (
			<div className={styles['payroll-work-types']}>
				<div className="input-header">
					<label>{label}</label>
				</div>

				<div className={styles['payroll-work-types__options']}>
					{workTypeOptions.map((option) => {
						const optionId = `${id}-${option.id}`;

						return (
							<div className={styles['payroll-work-types__option']} key={option.id}>
								<PlainCheckbox
									handleChange={handleWorkTypeChange(option.id)}
									id={optionId}
									isChecked={isSelected(option.id)}
									isDisabled={disabled || option.disabled}
									label={option.name}
								/>
							</div>
						);
					})}
				</div>
			</div>
		);
	}, []);

	return (
		<div className={formSectionClass}>
			<SegmentLabel
				label="Prevailing Wage Differential"
				tooltip="Settings and rate details related to Payroll Differential (PWD) tracking, labor activity selections, and MHA calculations for this job."
			/>

			<div className={formSectionClassRow}>
				<div className={unionPrevailingWageStyle}>
					<Field
						component={Checkbox}
						id="prevailingWageDeduction"
						isDisabled={isDisabled}
						label="Prevailing Wage Deduction"
						name={getAttributeName('payrollDifferential')}
					/>
				</div>

				{hasPrevailingWageDeduction && (
					<>
						<div className={formSectionClassColumn2}>
							<Field
								addonAfter="($/hr)"
								component={Input}
								disabled={isDisabled}
								id="payrollDifferentialValue"
								label="Differential Amount"
								name="payrollDifferential.value"
								placeholder="Enter Value"
								type="number"
								withThousandsSeparator={true}
							/>
						</div>

						<div className={formSectionClassColumn2}>
							<Field
								component={renderPayrollDifferentialWorkTypesCheckboxes}
								disabled={isDisabled}
								id="payrollDifferentialWorkTypes"
								label="Work Type"
								name="payrollDifferential.workTypes"
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const selector = formValueSelector(ownProps.formName);

	return {
		payrollDifferential: selector(state, getAttributeName('payrollDifferential')) as JobUpsertFM['payrollDifferential'],
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	React.memo
);

export default enhance(PrevailingWageDeductionSection);
